import { CommonModule } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { AzureService } from 'src/app/core/auth/azure.service'
import { User } from 'src/app/shared/models/user.model'
import { FormsModule } from '@angular/forms'
import { DropdownModule } from 'primeng/dropdown'
import { MultiSelectModule } from 'primeng/multiselect'
import { SliderModule } from 'primeng/slider'
import { CheckboxModule } from 'primeng/checkbox'
import { InputGroupModule } from 'primeng/inputgroup'
import { InputGroupAddonModule } from 'primeng/inputgroupaddon'
import { InputNumberModule } from 'primeng/inputnumber'
import { ButtonModule } from 'primeng/button'
import mappingGroups from './MappingGroups.json'
import { MappingGroup } from 'src/app/core/auth/azure.service'
import { SettingsService } from './services/settings.service'
import { MessageService } from 'primeng/api'
import { InputTextModule } from 'primeng/inputtext'
import { SettingsRule, Rule } from './models/settings.model'
import { getCodes, getName } from 'country-list'

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    MultiSelectModule,
    SliderModule,
    CheckboxModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    ButtonModule,
    InputTextModule,
  ],
})
export class Settings implements OnInit {
  private azureService = inject(AzureService)
  private settingsService = inject(SettingsService)
  private messageService = inject(MessageService)

  private readonly _destroying$ = new Subject<void>()

  currentUser: User | null = null
  userGroups: { label: string; value: string }[] = []
  selectedBusinessArea: string = ''
  mappingGroups: MappingGroup[] = mappingGroups
  rules: SettingsRule[] = []
  customers: any[] = []
  businessOptions: { label: string; value: string }[] = [
    { label: 'RME CP', value: 'RME CP' },
    { label: 'RME STG', value: 'RME STG' },
    { label: 'APAC CP', value: 'APAC CP' },
  ]
  vertical1Options: { label: string; value: string }[] = []
  vertical2Options: { label: string; value: string }[] = []
  countryOptions: { label: string; value: string }[] = []
  priceBandOptions = [
    { label: '< 1,000', value: [0, 999] },
    { label: '1,000 - 10,000', value: [1000, 9999] },
    { label: '10,000+', value: [10000] },
  ]

  get sortedRules(): SettingsRule[] {
    return [...this.rules].sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority
      }
      return this.rules.indexOf(a) - this.rules.indexOf(b)
    })
  }

  constructor() {
    this.azureService
      .getCurrentUser()
      .pipe(takeUntil(this._destroying$))
      .subscribe((userData) => {
        this.currentUser = userData
      })

      this.azureService.selectedBusinessArea.subscribe((businessArea) => {
        this.selectedBusinessArea = businessArea;
        this.getRules();
      });
  
    this.loadCustomers()
  }

  ngOnInit(): void {
    this.loadCountries()
    this.getRules()
  }

  loadCountries(): void {
    const codes = getCodes()
    this.countryOptions = codes.map((code: string) => ({
      label: getName(code) || code,
      value: code,
    }))
    console.log('Countries loaded:', this.countryOptions)
  }

  getRules() {
    this.settingsService.getRules(this.selectedBusinessArea).subscribe({
      next: (response) => {
        console.log('Rules:', response)
        this.rules = (response.rules || []).filter((rule: any) => rule.isdeleted !== true)
      },
      error: (error) => {
        console.error('Error getting rules:', error)
      },
    })
  }


  loadCustomers(): void {
    this.settingsService.getCustomers(this.selectedBusinessArea).subscribe({
      next: (response) => {
        console.log('Customers:', response)
        this.customers = response.customers
        
        const vertical1Set = new Set<string>()
        const vertical2Set = new Set<string>()
        
        response.customers.forEach((customer: any) => {
          if (customer.vertical1) vertical1Set.add(customer.vertical1)
          if (customer.vertical2) vertical2Set.add(customer.vertical2)
        })
        
        this.vertical1Options = Array.from(vertical1Set).map((v) => ({
          label: v,
          value: v,
        }))
        
        this.vertical2Options = Array.from(vertical2Set).map((v) => ({
          label: v,
          value: v,
        }))
      },
      error: (error) => {
        console.error('Error getting customers:', error)
      },
    })
  }

  /*  validateCapPctRange(rule: Rule): void {
    if (rule.capPctRange[0] !== null && rule.capPctRange[1] !== null) {
      if (rule.capPctRange[0] > rule.capPctRange[1]) {
        // If the minimum is greater than the maximum, reset the maximum
        rule.capPctRange[1] = rule.capPctRange[0]
      }
    }
  } */

  addRule(): void {
    const newRule: SettingsRule = {
      business: null,
      country: null,
      minimumMarginThreshold: null,
      capType: 'soft',
      thresholdPct: 15,
      capPct: null,
      capPctRange: [10, 15],
      customers: [],
      vertical1: null,
      vertical2: null,
      priceBand: [null, null],
      priority: 1,
      active: true,
      isEditing: true,
      isExpanded: true,
    }
    this.rules.push(newRule)
  }

  removeRule(index: number): void {
    this.rules.splice(this.rules.length - index - 1, 1)
    this.submitRules('remove')
  }

  toggleEdit(rule: SettingsRule): void {
    if (rule.isEditing) {
      console.log('Saving rule:', rule)
    }
    rule.isEditing = !rule.isEditing
  }

  toggleExpand(rule: SettingsRule): void {
    rule.isExpanded = !rule.isExpanded
  }

  submitRules(action?: string): void {
    this.rules.forEach((rule) => {
      rule.isEditing = false // Disable editing state
    })

    if (!this.validateRules()) return
    
    const submittedRules = this.rules.map(({ isEditing, ...rule }) => ({
      ...rule,
      customers: Array.isArray(rule.customers) 
        ? rule.customers.map((c: any) => typeof c === 'string' ? c : c.name)
        : []
    }))
    
    this.settingsService
      .createRules(this.selectedBusinessArea, submittedRules)
      .subscribe({
        next: (response) => {
          console.log('Response:', response)
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: action === 'remove' ? 'Rule removed!' : 'Rules changed!',
          })
          this.rules = response.rules
        },
        error: (error) => {
          console.error('Error submitting rules:', error)
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'There is some error while processing your request.',
          })
        },
      })

    console.log('Submitting Rules:', this.rules)
  }

  validateRules(): boolean {
    if (this.rules.length === 0) return true
    return this.rules.every((rule) => {
      if (rule.capType === 'hard') {
        if (rule.capPct === null || rule.capPct === undefined) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Cap Percentage is required for hard cap type.',
          })
          return false
        } else {
          rule.priceBand = [null, null]
          rule.capPctRange = []
        }
      } else {
        if (rule.capPctRange[0] === null || rule.capPctRange[1] === null) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Cap Percentage Range is required for soft cap type.',
          })
          return false
        }
        rule.capPct = null
      }

      if (rule.minimumMarginThreshold !== null && rule.minimumMarginThreshold !== undefined) {
        if (rule.minimumMarginThreshold < 0) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Minimum Margin Threshold must be a positive number.',
          })
          return false
        }
      }
      return true
    })
  }
}
